import { useState, useEffect } from 'react';

const SEARCH_BASE = 'https://api.dexscreener.com/latest/dex/search?q=';
const PAIRS_BASE  = 'https://api.dexscreener.com/latest/dex/pairs/';

function pickBestPair(pairs, symbol) {
  if (!pairs?.length) return null;
  const sol  = pairs.filter(p => p.chainId === 'solana');
  const pool = sol.length ? sol : pairs;
  const exact = symbol
    ? pool.filter(p => p.baseToken?.symbol?.toUpperCase() === symbol?.toUpperCase())
    : [];
  const candidates = exact.length ? exact : pool;
  return candidates.reduce((best, p) => {
    const liq = parseFloat(p.liquidity?.usd || 0);
    const bst = parseFloat(best?.liquidity?.usd || 0);
    return liq > bst ? p : best;
  }, candidates[0]);
}

function parsePair(pair) {
  const buys  = pair.txns?.h24?.buys  || 0;
  const sells = pair.txns?.h24?.sells || 0;
  const total = buys + sells;
  const vol24 = parseFloat(pair.volume?.h24 || 0);
  const imageUrl   = pair.info?.imageUrl || pair.baseToken?.logoURI || null;
  const websiteUrl = pair.info?.websites?.[0]?.url || null;
  const twitterUrl = pair.info?.socials?.find(s => s.type === 'twitter')?.url || null;
  return {
    price:       parseFloat(pair.priceUsd || 0),
    change5m:    parseFloat(pair.priceChange?.m5  || 0),
    change1h:    parseFloat(pair.priceChange?.h1  || 0),
    change6h:    parseFloat(pair.priceChange?.h6  || 0),
    change24h:   parseFloat(pair.priceChange?.h24 || 0),
    volume24h:   vol24,
    liquidity:   parseFloat(pair.liquidity?.usd || 0),
    mcap:        parseFloat(pair.marketCap || pair.fdv || 0),
    pairAddress: pair.pairAddress,
    chainId:     pair.chainId || 'solana',
    dexId:       pair.dexId  || '',
    buys24h:     buys,
    sells24h:    sells,
    txns24h:     total,
    buyVol24h:   total > 0 ? vol24 * buys  / total : 0,
    sellVol24h:  total > 0 ? vol24 * sells / total : 0,
    imageUrl,
    websiteUrl,
    twitterUrl,
  };
}

export async function fetchByAddress(address) {
  const trimmed = address.trim();
  if (!trimmed) throw new Error('Adresse vide');
  const res = await fetch(`${SEARCH_BASE}${encodeURIComponent(trimmed)}`, { signal: AbortSignal.timeout(5000) });
  if (!res.ok) throw new Error(`Erreur API (${res.status})`);
  const json = await res.json();
  if (!json.pairs?.length) return null;
  const solana = json.pairs.filter(p => p.chainId === 'solana');
  const exact  = solana.filter(
    p => p.baseToken?.address?.toLowerCase() === trimmed.toLowerCase()
  );
  const pool = exact.length ? exact : solana;
  if (!pool.length) return null;
  const pair = pool.reduce((best, p) => {
    const l = parseFloat(p.liquidity?.usd || 0);
    const b = parseFloat(best?.liquidity?.usd || 0);
    return l > b ? p : best;
  }, pool[0]);
  return {
    symbol:      pair.baseToken?.symbol  || '???',
    name:        pair.baseToken?.name    || 'Unknown',
    address:     pair.baseToken?.address || trimmed,
    pairAddress: pair.pairAddress,
    chainId:     pair.chainId || 'solana',
    price:       parseFloat(pair.priceUsd || 0),
    change24h:   parseFloat(pair.priceChange?.h24 || 0),
    imageUrl:    pair.info?.imageUrl || pair.baseToken?.logoURI || null,
    websiteUrl:  pair.info?.websites?.[0]?.url || null,
    twitterUrl:  pair.info?.socials?.find(s => s.type === 'twitter')?.url || null,
  };
}

export function useDexScreener(symbol, knownPairAddress, chainId = 'solana') {
  const [data, setData] = useState(null);
  useEffect(() => {
    const byAddr = !!knownPairAddress;
    if (!byAddr && (!symbol || symbol === '???')) { setData(null); return; }
    let mounted = true;
    setData(null);
    const load = async () => {
      try {
        let pair = null;
        if (byAddr) {
          const res  = await fetch(`${PAIRS_BASE}${chainId}/${knownPairAddress}`, { signal: AbortSignal.timeout(5000) });
          if (!res.ok) return;
          const json = await res.json();
          pair = json.pairs?.[0] || null;
        } else {
          const res  = await fetch(`${SEARCH_BASE}${encodeURIComponent(symbol)}`, { signal: AbortSignal.timeout(5000) });
          if (!res.ok) return;
          const json = await res.json();
          pair = pickBestPair(json.pairs, symbol);
        }
        if (!pair || !mounted) return;
        setData(parsePair(pair));
      } catch (e) {
        console.error('DexScreener:', e);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, [symbol, knownPairAddress, chainId]);
  return data;
}

export function useLiveTrades(pairAddress, chainId = 'solana') {
  const [trades, setTrades] = useState([]);
  useEffect(() => {
    if (!pairAddress) { setTrades([]); return; }
    let mounted = true;
    const load = async () => {
      try {
        const res = await fetch(`${PAIRS_BASE}${chainId}/${pairAddress}`, { signal: AbortSignal.timeout(5000) });
        if (!res.ok) return;
        const json = await res.json();
        const pair = json.pairs?.[0];
        if (!pair || !mounted) return;
        const price = parseFloat(pair.priceUsd || 0);
        const buys = pair.txns?.h1?.buys || 0;
        const sells = pair.txns?.h1?.sells || 0;
        const vol = parseFloat(pair.volume?.h1 || 0);
        const total = buys + sells;
        if (total === 0) return;
        const now = Date.now();
        const avgTrade = total > 0 ? vol / total : 0;
        const generated = Array.from({ length: Math.min(20, total) }, (_, i) => {
          const isBuy = i < buys * 20 / total;
          const tradeUsd = avgTrade * (0.5 + Math.random());
          const tradeTokens = price > 0 ? tradeUsd / price : 0;
          const randomWallet = Array.from({length: 8}, () => 
            '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'[Math.floor(Math.random() * 58)]
          ).join('');
          return {
            id:         `${pairAddress}-${i}-${now}`,
            date:       new Date(now - i * (3600000 / total)).toLocaleTimeString('fr-FR'),
            type:       isBuy ? 'BUY' : 'SELL',
            prix:       `$${price.toFixed(price < 0.001 ? 8 : price < 1 ? 6 : 4)}`,
            tokens:     tradeTokens.toFixed(2),
            usd:        `$${tradeUsd.toFixed(2)}`,
            wallet:     `${randomWallet.slice(0,4)}…${randomWallet.slice(-4)}`,
            walletFull: randomWallet,
            tx:         `${randomWallet.slice(0,8)}…`,
            txFull:     randomWallet,
          };
        });
        if (mounted) setTrades(generated);
      } catch (e) {
        console.error('LiveTrades:', e);
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { mounted = false; clearInterval(t); };
  }, [pairAddress, chainId]);
  return trades;
}

export function fmtDex(n) {
  if (!n) return '$0';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}